<template>
  <div class="page-container animate-fade-in-up">
    <!-- 列表视图 -->
    <template v-if="!detailMode">
      <PageHeader icon="el-icon-edit" title="员工离职风险行为修正" subtitle="通过已离职员工的行为回溯，优化 AI 预测模型的准确性" />

      <div class="table-wrapper">
        <el-table :data="resignedEmployees" stripe class="data-table" v-loading="false">
          <el-table-column label="姓名" min-width="140">
            <template slot-scope="scope">
              <div class="name-cell">
                <div class="employee-name">{{ scope.row.real_name }}</div>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="dept_name" label="部门" min-width="120">
            <template slot-scope="scope">{{ scope.row.dept_name || '-' }}</template>
          </el-table-column>
          <el-table-column prop="job_title" label="职位" min-width="120">
            <template slot-scope="scope">{{ scope.row.job_title || '-' }}</template>
          </el-table-column>
          <el-table-column label="离职日期" width="140" align="center">
            <template slot-scope="scope">
              <span class="resigned-date-cell">
                <i class="el-icon-date" style="margin-right: 4px; color: var(--color-text-muted);"></i>
                {{ scope.row.resignation_date }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="140" align="center">
            <template slot-scope="scope">
              <el-button
                size="mini"
                type="warning"
                round
                @click="openCorrection(scope.row)"
              >
                <i class="el-icon-edit"></i> 回溯验证
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <EmptyState v-if="resignedEmployees.length === 0" icon="el-icon-user" title="暂无可回溯验证的离职员工" hint="当前任务下没有已离职人员数据" />
    </template>

    <!-- 详情视图 -->
    <template v-else>
      <h1 class="visually-hidden">行为回溯验证</h1>
      <el-page-header
        @back="goBack"
        content="行为回溯验证"
        class="page-back-header"
      />

      <!-- 员工信息卡 -->
      <div class="employee-header-card">
        <div class="employee-avatar-large">
          {{ currentEmployee.real_name ? currentEmployee.real_name[0] : '?' }}
        </div>
        <div class="employee-details">
          <div class="employee-name-row">
            <h2 class="employee-name">{{ currentEmployee.real_name }}</h2>
          </div>
          <div class="employee-meta-text">
            {{ currentEmployee.dept_name || '-' }} · {{ currentEmployee.job_title || '-' }} · 离职日期：{{ currentEmployee.resignation_date || '-' }}
          </div>
        </div>
      </div>

      <div class="evaluation-body">
        <!-- 左侧行为评估区 -->
        <div class="evaluation-left">
          <h3 class="section-title">
            <i class="el-icon-edit-outline" style="color: var(--color-primary);"></i>
            行为回溯验证
          </h3>
          <p class="section-hint">
            <i class="el-icon-question" style="color: var(--color-text-muted);"></i>
            请确认该员工离职前1-3个月内，是否频繁表现出以下行为？
          </p>

          <!-- 按分类分组显示行为项 -->
          <div
            v-for="group in groupedBehaviors"
            :key="group.category_id"
            class="behavior-group"
          >
            <div class="behavior-group-title">
              <span class="group-name">{{ group.category_name }}</span>
            </div>

            <div class="behavior-group-card">
              <div
                v-for="(behavior, idx) in group.items"
                :key="behavior.behavior_id"
                class="behavior-item"
                :class="{ 'behavior-answered': corrections[behavior.behavior_id] !== undefined, 'behavior-last': idx === group.items.length - 1 }"
                @mouseenter="hoverBehavior = behavior"
              >
                <div class="behavior-header">
                  <span class="behavior-index">{{ group.globalIndex + idx + 1 }}.</span>
                  <div class="behavior-desc">{{ behavior.description }}</div>
                </div>

                <el-radio-group
                  v-model="corrections[behavior.behavior_id]"
                  class="behavior-options"
                >
                  <el-radio :label="true" class="option-yes">
                    <span class="option-label">是</span>
                  </el-radio>
                  <el-radio :label="false" class="option-no">
                    <span class="option-label">否</span>
                  </el-radio>
                </el-radio-group>
              </div>
            </div>
          </div>

          <!-- 自定义特征补充 -->
          <div class="correction-custom">
            <div class="correction-custom-title">
              <i class="el-icon-plus"></i> 自定义特征补充
            </div>
            <el-input
              type="textarea"
              v-model="customDesc"
              :rows="4"
              placeholder="除上方列出的行为外，您是否观察到其他异常举动？"
              maxlength="500"
              show-word-limit
            />
          </div>
        </div>

        <!-- 右侧辅助面板 -->
        <div class="evaluation-right">
          <div class="score-panel">
            <h4 class="sidebar-title">
              <i class="el-icon-info"></i> 行为特征客观辅助判断参考
            </h4>

            <div v-if="hoverBehavior" class="sidebar-content">
              <div class="sidebar-behavior-meta">
                分值 {{ hoverBehavior.score }} 分 · 相关性 {{ hoverBehavior.correlation_rate }}%
              </div>
              <div v-if="hoverBehavior.evidence_desc" class="sidebar-evidence">
                {{ hoverBehavior.evidence_desc }}
              </div>
              <div v-if="hoverBehavior.hint_info" class="sidebar-hint">
                <i class="el-icon-info"></i>
                <div>
                  <div style="font-weight: 600; margin-bottom: 4px;">判定指引</div>
                  <div>{{ hoverBehavior.hint_info }}</div>
                </div>
              </div>
            </div>
            <div v-else class="sidebar-empty">
              <i class="el-icon-mouse"></i>
              <div>鼠标悬停在行为项上查看辅助信息</div>
            </div>

            <!-- 进度条 -->
            <div class="progress-section">
              <div class="progress-label">
                评估进度
                <span class="progress-count">{{ answeredCount }} / {{ behaviors.length }}</span>
              </div>
              <el-progress
                :percentage="progressPercent"
                color="var(--color-medium)"
                :stroke-width="8"
                :show-text="false"
              />
            </div>

            <el-button
              type="primary"
              size="large"
              class="submit-btn btn-click"
              @click="submitCorrection"
            >
              <i class="el-icon-check"></i> 提交校验并记录
            </el-button>

            <div v-if="answeredCount < minRequired" class="submit-hint">
              <i class="el-icon-info"></i>
              为确保模型训练质量，请至少完成 {{ minRequired }} 项已知行为的判定后再提交
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import PageHeader from '../components/PageHeader.vue'
import EmptyState from '../components/EmptyState.vue'

export default {
  name: 'FeatureCorrection',
  components: { PageHeader, EmptyState },
  data() {
    return {
      detailMode: false,
      currentEmployee: {},
      corrections: {},
      customDesc: '',
      hoverBehavior: null
    }
  },
  computed: {
    resignedEmployees() {
      return this.$store.getters.taskResigned || []
    },
    behaviors() {
      return this.$store.getters.taskBehaviors || []
    },
    categories() {
      return this.$store.state.behaviors.categories || []
    },
    groupedBehaviors() {
      const groups = []
      let globalIndex = 0
      for (const cat of this.categories) {
        const items = this.behaviors.filter(b => b.category_id === cat.category_id)
        if (items.length) {
          groups.push({
            category_id: cat.category_id,
            category_name: cat.category_name,
            items,
            globalIndex
          })
          globalIndex += items.length
        }
      }
      const uncategorized = this.behaviors.filter(b => !this.categories.some(c => c.category_id === b.category_id))
      if (uncategorized.length) {
        groups.push({
          category_id: -1,
          category_name: '其他',
          items: uncategorized,
          globalIndex
        })
      }
      return groups
    },
    answeredCount() {
      return Object.values(this.corrections).filter(v => v !== undefined && v !== null).length
    },
    progressPercent() {
      if (!this.behaviors.length) return 0
      return Math.round((this.answeredCount / this.behaviors.length) * 100)
    },
    minRequired() {
      return Math.ceil(this.behaviors.length * 0.3)
    }
  },
  mounted() {
    const resignedName = this.$route.query.resigned
    if (resignedName) {
      this.$message.success(`${resignedName} 已标记为离职，请进行行为回溯验证`)
      // 清除 query 参数，避免刷新重复提示
      this.$router.replace({ path: '/correction' })
    }
  },
  methods: {
    categoryNameOf(categoryId) {
      const cat = this.categories.find(c => c.category_id === categoryId)
      return cat ? cat.category_name : '-'
    },
    openCorrection(emp) {
      this.currentEmployee = emp
      this.corrections = {}
      this.customDesc = ''
      this.hoverBehavior = null
      this.detailMode = true
    },
    goBack() {
      this.detailMode = false
      this.currentEmployee = {}
      this.corrections = {}
      this.customDesc = ''
      this.hoverBehavior = null
    },
    async submitCorrection() {
      const answered = Object.values(this.corrections).filter(v => v !== undefined).length
      const minRequired = Math.ceil(this.behaviors.length * 0.3)
      if (answered < minRequired) {
        this.$message.warning(`为确保模型训练质量，请至少完成 ${minRequired} 项已知行为的判定后再提交`)
        return
      }

      // 构造确认的行为ID列表
      const observedBehaviorIds = Object.entries(this.corrections)
        .filter(([_, v]) => v === true)
        .map(([k, _]) => parseInt(k))

      // 构造行为描述文本
      const confirmedBehaviors = this.behaviors
        .filter(b => this.corrections[b.behavior_id] === true)
        .map(b => b.description)
      let behaviorDetails = confirmedBehaviors.length
        ? '主管确认该员工离职前存在以下行为：' + confirmedBehaviors.join('；') + '。'
        : '主管未确认该员工存在明显异常行为。'
      if (this.customDesc) {
        behaviorDetails += ' 补充说明：' + this.customDesc
      }

      try {
        const res = await this.$api.createDepartureFeedback({
          employee_id: this.currentEmployee.employee_id,
          resignation_date: this.currentEmployee.resignation_date || null,
          observed_behaviors: observedBehaviorIds,
          behavior_details: behaviorDetails,
          suggestions: this.customDesc || ''
        })
        if (res.data) {
          // 同步到本地 store
          const feedback = {
            feedback_id: res.data.feedback_id || Date.now(),
            employee_id: this.currentEmployee.employee_id,
            employee_name: this.currentEmployee.real_name,
            manager_email: (this.$store.state.user && this.$store.state.user.email) || '',
            resignation_date: this.currentEmployee.resignation_date || '',
            observed_behaviors: observedBehaviorIds,
            behavior_details: behaviorDetails,
            suggestions: this.customDesc || '',
            created_at: new Date().toISOString().replace('T', ' ').slice(0, 19),
            task_id: (this.$store.state.tasks && this.$store.state.tasks.currentTask && this.$store.state.tasks.currentTask.task_id) || 1
          }
          this.$store.commit('ADD_FEEDBACK', feedback)
          this.$message.success('提交成功！数据已记录，将用于模型优化')
          this.goBack()
        }
      } catch (e) {
        this.$message.error('提交失败：' + ((e.response && e.response.data && e.response.data.message) || e.message || '未知错误'))
      }
    }
  }
}
</script>

<style scoped>
.page-container {
  max-width: 1200px;
  margin: 0 auto;
}

/* 列表视图 */
.table-wrapper {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: var(--shadow-sm);
}

.data-table >>> th {
  background: var(--color-bg) !important;
  color: var(--color-text) !important;
  font-weight: 600 !important;
}

.name-cell {
  display: flex;
  align-items: center;
  gap: var(--space-md);
}

.name-cell .employee-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text);
}

.resigned-date-cell {
  font-size: 13px;
  color: var(--color-text-secondary);
  display: inline-flex;
  align-items: center;
}

/* 详情视图：返回头 */
.page-back-header {
  margin-bottom: var(--space-xl);
}

/* 员工信息卡 */
.employee-header-card {
  display: flex;
  align-items: center;
  gap: var(--space-lg);
  padding: var(--space-xl);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  margin-bottom: var(--space-xl);
  box-shadow: var(--shadow-sm);
}

.employee-avatar-large {
  width: 64px;
  height: 64px;
  border-radius: var(--radius-full);
  background: var(--color-primary-gradient);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: 700;
  flex-shrink: 0;
}

.employee-details {
  flex: 1;
}

.employee-name-row {
  display: flex;
  align-items: center;
  gap: var(--space-md);
  margin-bottom: var(--space-xs);
}

.employee-header-card .employee-name {
  font-size: 20px;
  font-weight: 700;
  color: var(--color-text);
  margin: 0;
}

.employee-meta-text {
  font-size: 14px;
  color: var(--color-text-secondary);
}

/* 评估主体 */
.evaluation-body {
  display: flex;
  gap: var(--space-xl);
}

.evaluation-left {
  flex: 1;
  min-width: 0;
}

.evaluation-right {
  width: 300px;
  flex-shrink: 0;
}

/* 节标题 */
.section-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text);
  margin: 0 0 var(--space-sm) 0;
  display: flex;
  align-items: center;
  gap: var(--space-sm);
}

.section-hint {
  font-size: 14px;
  color: var(--color-text-secondary);
  margin: 0 0 var(--space-lg) 0;
  display: flex;
  align-items: center;
  gap: var(--space-sm);
}

/* 行为分组 */
.behavior-group {
  margin-bottom: var(--space-xl);
}

.behavior-group-title {
  display: flex;
  align-items: center;
  padding: var(--space-sm) var(--space-md);
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: var(--space-sm);
}

.behavior-group-title .group-name {
  color: var(--color-primary);
}

.behavior-group-card {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: var(--shadow-sm);
}

/* 行为项 */
.behavior-item {
  padding: var(--space-lg);
  border-bottom: 1px solid var(--color-border-light);
  transition: all var(--duration-normal) var(--ease-out);
}

.behavior-item:last-child,
.behavior-item.behavior-last {
  border-bottom: none;
}

.behavior-item:hover {
  background: var(--color-bg);
}

.behavior-answered {
  background: rgba(16, 185, 129, 0.04);
}

.behavior-header {
  display: flex;
  align-items: flex-start;
  gap: var(--space-sm);
}

.behavior-index {
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-muted);
  flex-shrink: 0;
  padding-top: 2px;
  min-width: 24px;
}

.behavior-desc {
  flex: 1;
  min-width: 0;
  font-size: 15px;
  font-weight: 600;
  color: var(--color-text);
  line-height: 1.5;
}

.behavior-options {
  padding-left: 32px;
  padding-top: var(--space-sm);
}

.behavior-options >>> .el-radio {
  margin-right: var(--space-xl);
  padding: var(--space-sm) var(--space-md);
  border-radius: var(--radius-md);
  transition: all var(--duration-fast) var(--ease-out);
}

.behavior-options >>> .el-radio:hover {
  background: var(--color-bg);
}

.option-label {
  font-weight: 600;
  margin-right: 4px;
}

/* 自定义特征补充 */
.correction-custom {
  margin-top: var(--space-xl);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: var(--space-lg);
  box-shadow: var(--shadow-sm);
}

.correction-custom-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: var(--space-md);
  display: flex;
  align-items: center;
  gap: var(--space-sm);
}

/* 右侧面板 */
.score-panel {
  position: sticky;
  top: var(--space-xl);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: var(--space-xl);
  box-shadow: var(--shadow-sm);
}

.sidebar-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text);
  margin: 0 0 var(--space-md) 0;
  display: flex;
  align-items: center;
  gap: var(--space-sm);
}

.sidebar-behavior-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: var(--space-sm);
}

.sidebar-behavior-meta {
  font-size: 12px;
  color: var(--color-text-secondary);
  margin-bottom: var(--space-md);
}

.sidebar-evidence {
  background: var(--color-bg);
  padding: var(--space-sm);
  border-radius: var(--radius-sm);
  font-size: 12px;
  color: var(--color-primary);
  margin-bottom: var(--space-md);
  line-height: 1.5;
}

.sidebar-hint {
  font-size: 12px;
  color: var(--color-text-secondary);
  display: flex;
  align-items: flex-start;
  gap: var(--space-xs);
  line-height: 1.5;
  background: var(--color-bg);
  padding: var(--space-sm);
  border-radius: var(--radius-sm);
}

.sidebar-hint i {
  color: var(--color-primary);
  margin-top: 2px;
  flex-shrink: 0;
}

.sidebar-empty {
  text-align: center;
  padding: var(--space-xl) 0;
  color: var(--color-text-muted);
}

.sidebar-empty i {
  font-size: 24px;
  margin-bottom: var(--space-sm);
  display: block;
}

/* 进度条 */
.progress-section {
  margin: var(--space-xl) 0;
}

.progress-label {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  color: var(--color-text-secondary);
  margin-bottom: var(--space-sm);
}

.progress-count {
  font-weight: 600;
  color: var(--color-text);
}

/* 提交按钮 */
.submit-btn {
  width: 100%;
  height: 48px;
  font-size: 16px;
  font-weight: 600;
}

.submit-hint {
  margin-top: var(--space-md);
  font-size: 12px;
  color: var(--color-text-muted);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-xs);
}
</style>
